import React, { useEffect, useState } from "react";
import * as icons from "lucide-react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useI18n } from "@/contexts/I18nContext";

function DynamicIcon({ name, className }) {
  const key = (name || "sparkles").replace(/(^|-)(\w)/g, (_, __, l) => l.toUpperCase());
  const Icon = icons[key] || icons.Sparkles;
  return <Icon className={className} />;
}

export default function Rewards() {
  const { t, lang } = useI18n();
  const [data, setData] = useState({ achievements: [], level: 1, xp: 0, next_level_xp: 100 });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rewards/achievements");
        setData(data);
      } catch (_) {}
    })();
  }, []);

  const xpProgress = Math.min(100, (data.xp % data.next_level_xp) / data.next_level_xp * 100);

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="font-display font-extrabold text-4xl tracking-tight">{t("rewards.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("rewards.subtitle")}</p>
      </div>

      <Card className="border-border mb-6">
        <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("dashboard.level")}</div>
            <div className="text-6xl font-display font-black text-primary">{data.level}</div>
          </div>
          <div className="flex-1 w-full">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{data.xp} XP</span>
              <span>{(data.level) * 100} XP</span>
            </div>
            <Progress value={xpProgress} data-testid="rewards-xp-bar" />
          </div>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.achievements.map((a) => (
          <Card key={a.code}
            className={`border-border ${a.unlocked ? "" : "opacity-60"}`}
            data-testid={`achievement-${a.code}`}>
            <CardContent className="p-6">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                a.unlocked ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
              }`}>
                <DynamicIcon name={a.icon} className="w-6 h-6" />
              </div>
              <div className="font-display font-bold text-lg">{lang === "fr" ? a.name_fr : a.name_en}</div>
              <div className="text-sm text-muted-foreground mt-1">{lang === "fr" ? a.description_fr : a.description_en}</div>
              <div className="mt-4">
                <Progress value={(a.current / a.threshold) * 100} />
                <div className="mt-2 text-xs text-muted-foreground">
                  {a.current} / {a.threshold} {a.unlocked ? `— ${t("rewards.unlocked")}` : ""}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </AppLayout>
  );
}
